// File: src/components/admin/IntegrationsView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Integrations configuration view (Stripe, SendGrid, Ad Platforms).
// Tabbed interface with URL-synchronized navigation.
// b: Enhanced with platform overview, health, status toggle, analytics.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Card, CardContent, TextField, CircularProgress,
    Alert, Snackbar, Typography, Grid, Divider, InputAdornment, Chip, Collapse, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogActions,
    Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
    Save,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Sync,
    ExpandMore,
    ExpandLess,
    Delete,
    Download,
    CreditCard as StripeIcon,
    Email as EmailIcon,
    Sms as SmsIcon,
    Campaign as AdPlatformIcon,
    Circle as CircleIcon,
    People as PeopleIcon,
    Chat as SlackIcon,
    Hub as McpIcon,
    PlayArrow as RunIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';
import StyledTabs, { TabPanel } from './common/StyledTabs';

// Tab configuration for URL mapping
const TAB_CONFIG = [
    { key: 'payments', index: 0, label: 'Payments', icon: <StripeIcon /> },
    { key: 'email', index: 1, label: 'Email', icon: <EmailIcon /> },
    { key: 'sms', index: 2, label: 'SMS / Twilio', icon: <SmsIcon /> },
    { key: 'ad-platforms', index: 3, label: 'Ad Platforms', icon: <AdPlatformIcon /> },
    { key: 'slack', index: 4, label: 'Slack', icon: <SlackIcon /> },
    { key: 'mcp-checker', index: 5, label: 'MCP Checker', icon: <McpIcon /> },
];

const PLATFORM_NAMES = {
    'google-ads': 'Google Ads',
    'meta-ads': 'Meta Ads',
    'microsoft-ads': 'Microsoft Ads',
};

const PLATFORM_COLORS = {
    'google-ads': 'primary',
    'meta-ads': 'info',
    'microsoft-ads': 'secondary',
};

const STATUS_COLORS = {
    active: 'success',
    beta: 'warning',
    disabled: 'default',
};

const HEALTH_COLORS = {
    healthy: 'success.main',
    degraded: 'warning.main',
    unavailable: 'error.main',
};

// --- Stripe Tab Component ---
const StripeTab = ({ config, onConfigChange, onSave, onVerify, saving, verifying, status, showSecret, onToggleSecret }) => {
    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Stripe Configuration</Typography>
                    <Button
                        startIcon={verifying ? <CircularProgress size={20} /> : (status === 'success' ? <CheckCircle /> : <Sync />)}
                        onClick={onVerify}
                        disabled={verifying || (!config.secretKey && !config.isConfigured)}
                        color={status === 'success' ? 'success' : 'primary'}
                    >
                        {verifying ? 'Verifying...' : 'Verify Connection'}
                    </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure Stripe for payment processing. You'll need your API keys from the Stripe Dashboard.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Publishable Key"
                            value={config.publishableKey || ''}
                            onChange={(e) => onConfigChange('publishableKey', e.target.value)}
                            helperText="pk_test_... or pk_live_..."
                            placeholder="pk_test_..."
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Secret Key"
                            value={config.secretKey || ''}
                            onChange={(e) => onConfigChange('secretKey', e.target.value)}
                            type={showSecret ? "text" : "password"}
                            helperText="sk_test_... or sk_live_..."
                            placeholder="sk_test_..."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button onClick={onToggleSecret} size="small">
                                            {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Webhook Signing Secret"
                            value={config.webhookSecret || ''}
                            onChange={(e) => onConfigChange('webhookSecret', e.target.value)}
                            type={showSecret ? "text" : "password"}
                            helperText="whsec_... - Used to verify webhook signatures"
                            placeholder="whsec_..."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                            onClick={onSave}
                            disabled={saving}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {saving ? 'Saving...' : 'Save Stripe Configuration'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// --- SendGrid Tab Component ---
const SendGridTab = ({ config, onConfigChange, onSave, onVerify, saving, verifying, status, showSecret, onToggleSecret }) => {
    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">SendGrid Configuration</Typography>
                    <Button
                        startIcon={verifying ? <CircularProgress size={20} /> : (status === 'success' ? <CheckCircle /> : <Sync />)}
                        onClick={onVerify}
                        disabled={verifying || (!config.apiKey && !config.isConfigured)}
                        color={status === 'success' ? 'success' : 'primary'}
                    >
                        {verifying ? 'Verifying...' : 'Verify Connection'}
                    </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure SendGrid for transactional emails. Get your API key from the SendGrid console.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="API Key"
                            value={config.apiKey || ''}
                            onChange={(e) => onConfigChange('apiKey', e.target.value)}
                            type={showSecret ? "text" : "password"}
                            helperText="Must start with SG."
                            placeholder="SG._..."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button onClick={onToggleSecret} size="small">
                                            {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                            onClick={onSave}
                            disabled={saving}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {saving ? 'Saving...' : 'Save SendGrid Configuration'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// Twilio Tab Component
const TwilioTab = ({ config, onConfigChange, onSave, onVerify, saving, verifying, status, showSecret, onToggleSecret }) => {
    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Twilio / SMS Configuration</Typography>
                    <Button
                        startIcon={verifying ? <CircularProgress size={20} /> : (status === 'success' ? <CheckCircle /> : <Sync />)}
                        onClick={onVerify}
                        disabled={verifying || (!config.accountSid && !config.isConfigured)}
                        color={status === 'success' ? 'success' : 'primary'}
                    >
                        {verifying ? 'Verifying...' : 'Verify Connection'}
                    </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure Twilio for SMS phone verification during free trial onboarding. Get your credentials from the Twilio console.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Account SID"
                            value={config.accountSid || ''}
                            onChange={(e) => onConfigChange('accountSid', e.target.value)}
                            type={showSecret ? "text" : "password"}
                            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button onClick={onToggleSecret} size="small">
                                            {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Auth Token"
                            value={config.authToken || ''}
                            onChange={(e) => onConfigChange('authToken', e.target.value)}
                            type={showSecret ? "text" : "password"}
                            placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button onClick={onToggleSecret} size="small">
                                            {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Phone Number (Sender)"
                            value={config.phoneNumber || ''}
                            onChange={(e) => onConfigChange('phoneNumber', e.target.value)}
                            placeholder="+15551234567"
                            helperText="E.164 format (e.g., +15551234567)"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                            onClick={onSave}
                            disabled={saving}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {saving ? 'Saving...' : 'Save Twilio Configuration'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// Platform Summary Grid (b AC1)
const PlatformSummaryGrid = ({ platforms, healthData, onStatusChange, onToggleExpanded, statusSaving }) => {
    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell><strong>Platform</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Connected</strong></TableCell>
                        <TableCell><strong>API Health</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(platforms).map(([platformId, platform]) => {
                        const health = healthData[platformId] || {};
                        const healthStatus = health.status || (platform.isConfigured ? 'healthy' : 'N/A');
                        const isPrimary = platformId === 'google-ads';

                        return (
                            <TableRow key={platformId} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {PLATFORM_NAMES[platformId] || platformId}
                                        </Typography>
                                        {platform.isConfigured && (
                                            <Chip label="Configured" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={isPrimary ? `${PLATFORM_NAMES[platformId] || platformId} is the primary platform and cannot be disabled` : ''}>
                                        <FormControl size="small" sx={{ minWidth: 110 }}>
                                            <Select
                                                value={platform.status || 'active'}
                                                onChange={(e) => onStatusChange(platformId, e.target.value)}
                                                disabled={isPrimary || statusSaving[platformId]}
                                                size="small"
                                                sx={{ fontSize: '0.8rem' }}
                                                data-testid={`platform-status-select-${platformId}`}
                                            >
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="beta">Beta</MenuItem>
                                                <MenuItem value="disabled">Disabled</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        <PeopleIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {(platform.connectedUsers || 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {platform.isConfigured ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CircleIcon sx={{ fontSize: 10, color: HEALTH_COLORS[healthStatus] || 'grey.400' }} />
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                {healthStatus}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled">N/A</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        onClick={() => onToggleExpanded(platformId)}
                                    >
                                        Configure
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Ad Platforms Tab Component (b enhanced)
const AdPlatformsTab = ({
    platforms,
    formData,
    expanded,
    saving,
    verifying,
    exporting,
    status,
    showSecrets,
    healthData,
    analytics,
    statusSaving,
    onFormChange,
    onSave,
    onVerify,
    onDelete,
    onExport,
    onToggleExpanded,
    onToggleSecret,
    onStatusChange,
}) => {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure API credentials for ad platform integrations. These credentials are encrypted and securely stored.
                Enable specific platforms per subscription plan in the Subscription Options configuration.
            </Typography>

            {/* Platform Summary Grid (AC1) */}
            <PlatformSummaryGrid
                platforms={platforms}
                healthData={healthData}
                onStatusChange={onStatusChange}
                onToggleExpanded={onToggleExpanded}
                statusSaving={statusSaving}
            />

            {/* Platform Configuration Cards */}
            <Grid container spacing={3}>
                {Object.entries(platforms).map(([platformId, platform]) => (
                    <Grid item xs={12} lg={6} key={platformId}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6">{PLATFORM_NAMES[platformId] || platformId}</Typography>
                                        <Chip
                                            label={(platform.status || 'active').charAt(0).toUpperCase() + (platform.status || 'active').slice(1)}
                                            size="small"
                                            color={STATUS_COLORS[platform.status || 'active']}
                                        />
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => onToggleExpanded(platformId)}
                                    >
                                        {expanded[platformId] ? <ExpandLess /> : <ExpandMore />}
                                    </Button>
                                </Box>

                                {/* Health indicators (AC3) */}
                                {platform.isConfigured && healthData[platformId] && (
                                    <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            API: <Box component="span" sx={{ color: HEALTH_COLORS[healthData[platformId].status] || 'grey.500' }}>
                                                {healthData[platformId].status || 'Unknown'}
                                            </Box>
                                        </Typography>
                                        {healthData[platformId].connectedUsers > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                Users: {healthData[platformId].connectedUsers}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {platform.updatedAt && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Last updated: {new Date(platform.updatedAt).toLocaleString()}
                                        {platform.updatedBy && ` by ${platform.updatedBy}`}
                                    </Typography>
                                )}

                                <Collapse in={expanded[platformId]}>
                                    <Divider sx={{ my: 2 }} />

                                    {platformId === 'google-ads' && (
                                        <Alert severity="info" sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                                Configuring Google Ads
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                To configure Google Ads integration, you need to provide your OAuth 2.0 Client credentials and Developer Token.
                                                Ensure you have enabled the Google Ads API in your Google Cloud Console project.
                                            </Typography>
                                            <Button
                                                size="small"
                                                color="inherit"
                                                variant="outlined"
                                                href="https://developers.google.com/google-ads/api/docs/start"
                                                target="_blank"
                                                sx={{ mt: 1 }}
                                            >
                                                View Documentation
                                            </Button>
                                        </Alert>
                                    )}

                                    {platformId === 'microsoft-ads' && (
                                        <Alert severity="info" sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                                Configuring Microsoft Ads
                                            </Typography>
                                            <Typography variant="body2">
                                                Register an app in Azure Entra ID (formerly Azure AD) to get your Client ID and Secret.
                                                Add a Developer Token from the Microsoft Advertising portal.
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Grid container spacing={2}>
                                        {Object.entries(platform.schema || {}).map(([field, fieldSchema]) => (
                                            <Grid item xs={12} key={field}>
                                                <TextField
                                                    fullWidth
                                                    label={fieldSchema.label}
                                                    value={formData[platformId]?.[field] || ''}
                                                    onChange={(e) => onFormChange(platformId, field, e.target.value)}
                                                    type={fieldSchema.type === 'secret' && !showSecrets[`${platformId}-${field}`] ? 'password' : 'text'}
                                                    required={fieldSchema.required}
                                                    size="small"
                                                    placeholder={fieldSchema.type === 'secret' && platform.config?.[`${field}Configured`] ? '********' : ''}
                                                    InputProps={fieldSchema.type === 'secret' ? {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Button
                                                                    onClick={() => onToggleSecret(platformId, field)}
                                                                    size="small"
                                                                >
                                                                    {showSecrets[`${platformId}-${field}`] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                </Button>
                                                            </InputAdornment>
                                                        )
                                                    } : undefined}
                                                />
                                            </Grid>
                                        ))}

                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color={PLATFORM_COLORS[platformId] || 'primary'}
                                                    startIcon={saving[platformId] ? <CircularProgress size={20} /> : <Save />}
                                                    onClick={() => onSave(platformId)}
                                                    disabled={saving[platformId]}
                                                    sx={{ flex: 1 }}
                                                >
                                                    {saving[platformId] ? 'Saving...' : 'Save'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={verifying[platformId] ? <CircularProgress size={20} /> : (status[platformId] === 'success' ? <CheckCircle /> : <Sync />)}
                                                    onClick={() => onVerify(platformId)}
                                                    disabled={verifying[platformId] || !platform.isConfigured}
                                                    color={status[platformId] === 'success' ? 'success' : 'primary'}
                                                >
                                                    Verify
                                                </Button>
                                                {platform.isConfigured && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={exporting[platformId] ? <CircularProgress size={20} /> : <Download />}
                                                        onClick={() => onExport(platformId)}
                                                        disabled={exporting[platformId]}
                                                        title="Export full config with secrets and user OAuth data"
                                                    >
                                                        Export
                                                    </Button>
                                                )}
                                                {platform.isConfigured && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => onDelete(platformId)}
                                                    >
                                                        <Delete />
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Platform Analytics (AC5) */}
            {analytics && Object.keys(analytics.platforms || {}).length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Platform Analytics</Typography>
                    <Grid container spacing={2}>
                        {Object.entries(analytics.platforms || {}).map(([platformId, data]) => (
                            <Grid item xs={12} md={4} key={platformId}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                        {PLATFORM_NAMES[platformId] || platformId}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Connections: {data.connections || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Imports: {data.syncOps?.imports || 0} | Exports: {data.syncOps?.exports || 0}
                                    </Typography>
                                    {data.syncOps?.failures > 0 && (
                                        <Typography variant="body2" color="error.main">
                                            Failures: {data.syncOps.failures}
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

// Category labels for event grouping
const CATEGORY_LABELS = {
    subscription: 'Subscription & Billing',
    trials: 'Trials',
    ai: 'AI & Agent',
    autopilot: 'Autopilot',
    sync: 'Sync & Data',
    connections: 'Connections',
    campaigns: 'Campaigns',
    system: 'System',
};

const CATEGORY_ORDER = ['subscription', 'trials', 'ai', 'autopilot', 'sync', 'connections', 'campaigns', 'system'];

// Grouped Event Config Card
const SlackEventConfigCard = ({ eventConfig, onConfigChange }) => {
    const [expandedCategories, setExpandedCategories] = React.useState({});

    // Group events by category
    const grouped = {};
    eventConfig.forEach((event, idx) => {
        const cat = event.category || 'system';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push({ ...event, _idx: idx });
    });

    const toggleCategory = (cat) => {
        setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
    };

    const handleBulkToggle = (cat, enabled) => {
        const updated = [...eventConfig];
        grouped[cat].forEach(event => {
            updated[event._idx] = { ...updated[event._idx], enabled };
        });
        onConfigChange('eventConfig', updated);
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Event Notifications</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure which events send Slack notifications and to which channel. Events are grouped by category.
                </Typography>

                {CATEGORY_ORDER.filter(cat => grouped[cat]).map(cat => {
                    const events = grouped[cat];
                    const enabledCount = events.filter(e => e.enabled !== false).length;
                    const isExpanded = expandedCategories[cat] !== false; // default expanded

                    return (
                        <Box key={cat} sx={{ mb: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    bgcolor: 'grey.50', px: 2, py: 1, borderRadius: 1, cursor: 'pointer',
                                }}
                                onClick={() => toggleCategory(cat)}
                                data-testid={`slack-category-header-${cat}`}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {CATEGORY_LABELS[cat] || cat}
                                    </Typography>
                                    <Chip label={`${enabledCount}/${events.length}`} size="small" variant="outlined" />
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={enabledCount === events.length}
                                                indeterminate={enabledCount > 0 && enabledCount < events.length}
                                                onChange={(e) => handleBulkToggle(cat, e.target.checked)}
                                                data-testid={`slack-category-toggle-${cat}`}
                                            />
                                        }
                                        label={<Typography variant="caption">All</Typography>}
                                    />
                                </Box>
                            </Box>

                            <Collapse in={isExpanded}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableBody>
                                            {events.map(event => (
                                                <TableRow key={event.key}>
                                                    <TableCell sx={{ pl: 4 }}>
                                                        <Typography variant="body2" fontWeight={500}>{event.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{event.key}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={event.channel || ''}
                                                            onChange={(e) => {
                                                                const updated = [...eventConfig];
                                                                updated[event._idx] = { ...updated[event._idx], channel: e.target.value };
                                                                onConfigChange('eventConfig', updated);
                                                            }}
                                                            placeholder="#channel"
                                                            sx={{ minWidth: 160 }}
                                                            data-testid={`slack-event-channel-${event.key}`}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Switch
                                                            checked={event.enabled !== false}
                                                            onChange={(e) => {
                                                                const updated = [...eventConfig];
                                                                updated[event._idx] = { ...updated[event._idx], enabled: e.target.checked };
                                                                onConfigChange('eventConfig', updated);
                                                            }}
                                                            size="small"
                                                            data-testid={`slack-event-toggle-${event.key}`}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Box>
                    );
                })}
            </CardContent>
        </Card>
    );
};

// Slack Tab Component
const SlackTab = ({
    config,
    onConfigChange,
    onSave,
    onVerify,
    saving,
    verifying,
    status,
    showSecret,
    onToggleSecret,
    notificationHistory,
    onLoadHistory,
    loadingHistory,
}) => {
    return (
        <Box>
            {/* Connection Config Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">Slack Configuration</Typography>
                            {config.isConfigured && (
                                <Chip
                                    label={config.enabled ? 'Active' : 'Disabled'}
                                    size="small"
                                    color={config.enabled ? 'success' : 'default'}
                                />
                            )}
                        </Box>
                        <Button
                            startIcon={verifying ? <CircularProgress size={20} /> : (status === 'success' ? <CheckCircle /> : <Sync />)}
                            onClick={onVerify}
                            disabled={verifying || (!config.botToken && !config.isConfigured)}
                            color={status === 'success' ? 'success' : 'primary'}
                            data-testid="slack-verify-btn"
                        >
                            {verifying ? 'Testing...' : 'Test Connection'}
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure Slack to receive notifications for key events (sign-ups, subscriptions, trials).
                        Create a Slack App with <code>chat:write</code> and <code>channels:read</code> scopes, install it to your workspace, and paste the Bot Token below.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bot Token"
                                value={config.botToken || ''}
                                onChange={(e) => onConfigChange('botToken', e.target.value)}
                                type={showSecret ? "text" : "password"}
                                helperText="xoxb-... (Bot User OAuth Token)"
                                placeholder="xoxb-..."
                                data-testid="slack-bot-token"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button onClick={onToggleSecret} size="small">
                                                {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </Button>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Default Channel"
                                value={config.defaultChannel || ''}
                                onChange={(e) => onConfigChange('defaultChannel', e.target.value)}
                                helperText="Fallback channel (e.g. #general)"
                                placeholder="#ops-notifications"
                                data-testid="slack-default-channel"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.enabled || false}
                                        onChange={(e) => onConfigChange('enabled', e.target.checked)}
                                        data-testid="slack-enabled-toggle"
                                    />
                                }
                                label="Enable Slack notifications"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                                onClick={onSave}
                                disabled={saving}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                data-testid="slack-save-btn"
                            >
                                {saving ? 'Saving...' : 'Save Slack Configuration'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            { /* Grouped Event Configuration Card */}
            {config.eventConfig && config.eventConfig.length > 0 && (
                <SlackEventConfigCard eventConfig={config.eventConfig} onConfigChange={onConfigChange} />
            )}

            {/* Notification History Card */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Recent Notifications</Typography>
                        <Button
                            size="small"
                            onClick={onLoadHistory}
                            disabled={loadingHistory}
                            startIcon={loadingHistory ? <CircularProgress size={16} /> : <Sync />}
                            data-testid="slack-load-history-btn"
                        >
                            {loadingHistory ? 'Loading...' : 'Refresh'}
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {notificationHistory.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No notifications sent yet. Notifications will appear here once Slack is configured and events occur.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Event</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Channel</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {notificationHistory.map((entry, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {new Date(entry.timestamp).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{entry.eventType}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{entry.email || entry.userName || '—'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{entry.channel}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={entry.success ? 'Sent' : 'Failed'}
                                                    size="small"
                                                    color={entry.success ? 'success' : 'error'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

// --- Main IntegrationsView Component ---
// MCP Checker Tab
// Diagnoses the remote MCP server (Streamable HTTP + OAuth) end-to-end from the
// admin app. fetch uses credentials:'include' so the Cloudflare Access session
// cookie rides along on staging (the endpoint is Access-gated), plus a DefoAds
// `dads_` token for the MCP manual-token auth path. The MCP worker allow-lists
// this admin origin for CORS and Access bypasses the preflight, so the
// cross-origin call succeeds once you're signed into Access in this browser.
const DEFAULT_MCP_URL = 'https://mcp-ads-staging.defoai.com';

const MCP_STEP_LABELS = {
    health: 'GET /healthz — reachability + Access',
    initialize: 'initialize — protocol handshake',
    tools: 'tools/list — tool catalog',
    call: 'tools/call → list_campaigns — authed read',
};

const MCP_STATUS_COLOR = { pass: 'success', warn: 'warning', fail: 'error', pending: 'default' };

const accessHint = (base) =>
    `Got a non-JSON response — likely the Cloudflare Access login page. Open ${base} in this browser, sign in with an allowed email, then retry.`;
const corsHint =
    'Blocked before reaching the server — almost always means you have no Cloudflare Access session for the MCP host yet, so the request is redirected to the Access login (which a fetch cannot complete). Click “Sign in to MCP host”, complete the login in the new tab, then run the check again.';

const McpCheckerTab = () => {
    const [mcpUrl, setMcpUrl] = useState(DEFAULT_MCP_URL);
    const [token, setToken] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [running, setRunning] = useState(false);
    const [steps, setSteps] = useState([]);
    const [expanded, setExpanded] = useState({});

    const base = () => mcpUrl.trim().replace(/\/+$/, '');

    const parseResponse = async (res) => {
        const ct = res.headers.get('content-type') || '';
        const text = await res.text();
        if (!ct.includes('application/json')) {
            return { ok: false, status: res.status, nonJson: true, raw: text.slice(0, 600) };
        }
        let json = null;
        try { json = JSON.parse(text); } catch { /* leave null */ }
        return { ok: res.ok, status: res.status, json };
    };

    const rpc = (method, params, id) =>
        fetch(`${base()}/mcp`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, text/event-stream',
                ...(token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {}),
            },
            body: JSON.stringify({ jsonrpc: '2.0', id, method, params }),
        }).then(parseResponse);

    const hintFor = (p) => (p.nonJson ? accessHint(base()) : `HTTP ${p.status}${p.json?.error?.message ? ` — ${p.json.error.message}` : ''}`);

    const runCheck = async () => {
        setRunning(true);
        setExpanded({});
        const results = [];
        const push = (s) => { results.push(s); setSteps([...results]); };

        // 1) health — also the quickest Access / reachability probe.
        try {
            const p = await parseResponse(await fetch(`${base()}/healthz`, { credentials: 'include' }));
            if (p.nonJson) push({ key: 'health', status: 'fail', detail: accessHint(base()), data: p.raw });
            else push({ key: 'health', status: p.json?.ok ? 'pass' : 'fail', detail: `HTTP ${p.status}`, data: p.json });
        } catch (e) {
            push({ key: 'health', status: 'fail', detail: corsHint, data: String(e) });
            setRunning(false);
            return; // if we can't even reach /healthz, the RPC steps will all fail the same way
        }

        // 2) initialize
        try {
            const p = await rpc('initialize', {
                protocolVersion: '2025-06-18',
                capabilities: {},
                clientInfo: { name: 'admin-mcp-checker', version: '1.0' },
            }, 1);
            const ok = p.json?.result?.serverInfo?.name === 'defoads';
            push({ key: 'initialize', status: ok ? 'pass' : 'fail', detail: ok ? `protocol ${p.json.result.protocolVersion}` : hintFor(p), data: p.json ?? p.raw });
        } catch (e) { push({ key: 'initialize', status: 'fail', detail: corsHint, data: String(e) }); }

        // 3) tools/list
        try {
            const p = await rpc('tools/list', {}, 2);
            const tools = p.json?.result?.tools;
            const ok = Array.isArray(tools) && tools.length > 0;
            push({ key: 'tools', status: ok ? 'pass' : 'fail', detail: ok ? `${tools.length} tools advertised` : hintFor(p), data: ok ? tools.map((t) => t.name) : (p.json ?? p.raw) });
        } catch (e) { push({ key: 'tools', status: 'fail', detail: corsHint, data: String(e) }); }

        // 4) tools/call → list_campaigns. A typed auth error still proves the
        // endpoint works (reached + responded) — surface it as a warning, not a
        // hard fail, so a missing/invalid token is distinguishable from a broken
        // deploy.
        try {
            const p = await rpc('tools/call', { name: 'list_campaigns', arguments: {} }, 3);
            const text = p.json?.result?.content?.[0]?.text;
            let env = null;
            try { env = text ? JSON.parse(text) : null; } catch { /* leave null */ }
            if (env?.ok) {
                push({ key: 'call', status: 'pass', detail: `ok — ${Array.isArray(env.data) ? env.data.length : '?'} campaigns`, data: env });
            } else if (env?.error) {
                push({ key: 'call', status: 'warn', detail: `endpoint reached, token rejected: ${env.error.type}${env.error.code ? ` (${env.error.code})` : ''}`, data: env });
            } else {
                push({ key: 'call', status: 'fail', detail: hintFor(p), data: p.json ?? p.raw });
            }
        } catch (e) { push({ key: 'call', status: 'fail', detail: corsHint, data: String(e) }); }

        setRunning(false);
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Checks the remote MCP server end-to-end: reachability, the Streamable-HTTP handshake,
                the tool catalog, and an authenticated read. Requests send credentials, so on staging
                the MCP host is behind Cloudflare Access — <strong>click “Sign in to MCP host” first</strong>,
                complete the login in the new tab, then run the check (a <code>fetch</code> can’t do the
                Access login itself, so without a session the call fails with an opaque CORS error). The
                <strong> token</strong> is the manual-auth fallback (a DefoAds <code>dads_</code> token);
                leave it blank to test reachability + discovery only.
            </Alert>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); runCheck(); }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth label="MCP base URL" value={mcpUrl}
                            onChange={(e) => setMcpUrl(e.target.value)}
                            size="small" placeholder={DEFAULT_MCP_URL}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth label="DefoAds token (dads_…, optional)" value={token}
                            onChange={(e) => setToken(e.target.value)}
                            size="small" type={showToken ? 'text' : 'password'}
                            autoComplete="off"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button onClick={() => setShowToken(!showToken)} size="small" sx={{ minWidth: 0 }} tabIndex={-1}>
                                            {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        type="submit" variant="contained" disabled={running || !mcpUrl.trim()}
                        startIcon={running ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                    >
                        {running ? 'Running…' : 'Run MCP check'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => window.open(`${base()}/healthz`, '_blank', 'noopener,noreferrer')}
                        disabled={!mcpUrl.trim()}
                        startIcon={<OpenInNewIcon />}
                    >
                        Sign in to MCP host
                    </Button>
                </Box>
            </Box>

            {steps.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    {steps.map((s) => (
                        <Paper key={s.key} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                                onClick={() => setExpanded((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
                            >
                                <Chip
                                    label={s.status === 'warn' ? 'WARN' : s.status.toUpperCase()}
                                    color={MCP_STATUS_COLOR[s.status] || 'default'}
                                    size="small" sx={{ minWidth: 64 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{MCP_STEP_LABELS[s.key] || s.key}</Typography>
                                    <Typography variant="caption" color="text.secondary">{s.detail}</Typography>
                                </Box>
                                {expanded[s.key] ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                            <Collapse in={!!expanded[s.key]}>
                                <Box
                                    component="pre"
                                    sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 280 }}
                                >
                                    {typeof s.data === 'string' ? s.data : JSON.stringify(s.data, null, 2)}
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const IntegrationsView = () => {
    const { tab } = useParams();
    const navigate = useNavigate();

    // Determine active tab from URL param
    const getActiveTab = () => {
        const tabConfig = TAB_CONFIG.find(t => t.key === tab);
        return tabConfig ? tabConfig.index : 0; // Default to Payments tab
    };

    const activeTab = getActiveTab();

    const handleTabChange = (event, newValue) => {
        const tabConfig = TAB_CONFIG.find(t => t.index === newValue);
        if (tabConfig) {
            navigate(`/integrations/${tabConfig.key}`);
        }
    };

    // Stripe State
    const [stripeConfig, setStripeConfig] = useState({
        publishableKey: '',
        secretKey: '',
        webhookSecret: '',
    });
    const [stripeSaving, setStripeSaving] = useState(false);
    const [stripeVerifying, setStripeVerifying] = useState(false);
    const [stripeStatus, setStripeStatus] = useState(null);
    const [showStripeSecret, setShowStripeSecret] = useState(false);

    // SendGrid State
    const [sendgridConfig, setSendgridConfig] = useState({
        apiKey: '',
    });
    const [sendgridSaving, setSendgridSaving] = useState(false);
    const [sendgridVerifying, setSendgridVerifying] = useState(false);
    const [sendgridStatus, setSendgridStatus] = useState(null);
    const [showSendgridKey, setShowSendgridKey] = useState(false);

    // Twilio State
    const [twilioConfig, setTwilioConfig] = useState({ accountSid: '', authToken: '', phoneNumber: '' });
    const [twilioSaving, setTwilioSaving] = useState(false);
    const [twilioVerifying, setTwilioVerifying] = useState(false);
    const [twilioStatus, setTwilioStatus] = useState(null);
    const [showTwilioSecret, setShowTwilioSecret] = useState(false);

    // Slack State
    const [slackConfig, setSlackConfig] = useState({ botToken: '', defaultChannel: '#general', enabled: false, eventConfig: [] });
    const [slackSaving, setSlackSaving] = useState(false);
    const [slackVerifying, setSlackVerifying] = useState(false);
    const [slackStatus, setSlackStatus] = useState(null);
    const [showSlackSecret, setShowSlackSecret] = useState(false);
    const [slackHistory, setSlackHistory] = useState([]);
    const [slackHistoryLoading, setSlackHistoryLoading] = useState(false);

    // Ad Platform Integration State
    const [adPlatforms, setAdPlatforms] = useState({});
    const [adPlatformExpanded, setAdPlatformExpanded] = useState({});
    const [adPlatformSaving, setAdPlatformSaving] = useState({});
    const [adPlatformVerifying, setAdPlatformVerifying] = useState({});
    const [adPlatformExporting, setAdPlatformExporting] = useState({});
    const [adPlatformStatus, setAdPlatformStatus] = useState({});
    const [showAdPlatformSecrets, setShowAdPlatformSecrets] = useState({});
    const [adPlatformFormData, setAdPlatformFormData] = useState({});

    // b: Health and Analytics State
    const [healthData, setHealthData] = useState({});
    const [analytics, setAnalytics] = useState(null);
    const [statusSaving, setStatusSaving] = useState({});
    const [disableConfirmDialog, setDisableConfirmDialog] = useState(null);

    // Common State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const loadConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [stripeData, sendgridData, twilioData, adPlatformData, slackData] = await Promise.all([
                adminApi.getStripeConfig(),
                adminApi.getSendGridConfig(),
                adminApi.getTwilioConfig(),
                adminApi.getAdPlatformConfigs(),
                adminApi.getSlackConfig()
            ]);

            setStripeConfig({
                publishableKey: stripeData.publishableKey,
                secretKey: stripeData.secretKey,
                webhookSecret: stripeData.webhookSecret,
                isConfigured: stripeData.isConfigured
            });

            setSendgridConfig({
                apiKey: sendgridData.apiKey,
                isConfigured: sendgridData.isConfigured
            });

            setTwilioConfig({
                accountSid: twilioData.accountSid,
                authToken: twilioData.authToken,
                phoneNumber: twilioData.phoneNumber,
                isConfigured: twilioData.isConfigured
            });

            // Slack config
            setSlackConfig({
                botToken: slackData.botToken,
                defaultChannel: slackData.defaultChannel || '#general',
                enabled: slackData.enabled,
                isConfigured: slackData.isConfigured,
                eventConfig: slackData.eventConfig || [],
            });

            // Initialize ad platform state
            if (adPlatformData?.integrations) {
                setAdPlatforms(adPlatformData.integrations);
                const formData = {};
                Object.entries(adPlatformData.integrations).forEach(([platformId, platform]) => {
                    formData[platformId] = { ...platform.config };
                });
                setAdPlatformFormData(formData);

                // Load health data for configured platforms
                const healthPromises = {};
                for (const [platformId, platform] of Object.entries(adPlatformData.integrations)) {
                    if (platform.isConfigured) {
                        healthPromises[platformId] = adminApi.getPlatformHealth(platformId)
                            .then(r => r.data)
                            .catch(() => ({ status: 'unknown', connectedUsers: platform.connectedUsers || 0 }));
                    }
                }
                const healthResults = {};
                for (const [pid, promise] of Object.entries(healthPromises)) {
                    healthResults[pid] = await promise;
                }
                setHealthData(healthResults);

                // Load analytics
                try {
                    const analyticsData = await adminApi.getPlatformAnalytics({ period: '7d' });
                    setAnalytics(analyticsData.data);
                } catch {
                    // Analytics is non-critical
                }
            }

        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfigs();
    }, [loadConfigs]);

    // --- Stripe Handlers ---
    const handleStripeChange = (field, value) => {
        setStripeConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleVerifyStripe = async () => {
        setStripeVerifying(true);
        setStripeStatus(null);
        try {
            await adminApi.verifyStripeConnection({ secretKey: stripeConfig.secretKey });
            setStripeStatus('success');
            setSnackbar({ open: true, message: 'Stripe connection verified successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setStripeStatus('error');
            setSnackbar({ open: true, message: `Stripe verification failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setStripeVerifying(false);
        }
    };

    const handleSaveStripe = async () => {
        setStripeSaving(true);
        try {
            await adminApi.updateStripeConfig(stripeConfig);
            setSnackbar({ open: true, message: 'Stripe configuration updated successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Stripe update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setStripeSaving(false);
        }
    };

    // --- SendGrid Handlers ---
    const handleSendGridChange = (field, value) => {
        setSendgridConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleVerifySendGrid = async () => {
        setSendgridVerifying(true);
        setSendgridStatus(null);
        try {
            await adminApi.verifySendGridConnection({ apiKey: sendgridConfig.apiKey });
            setSendgridStatus('success');
            setSnackbar({ open: true, message: 'SendGrid connection verified successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSendgridStatus('error');
            setSnackbar({ open: true, message: `SendGrid verification failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSendgridVerifying(false);
        }
    };

    const handleSaveSendGrid = async () => {
        setSendgridSaving(true);
        try {
            await adminApi.updateSendGridConfig(sendgridConfig);
            setSnackbar({ open: true, message: 'SendGrid configuration updated successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `SendGrid update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSendgridSaving(false);
        }
    };

    // Twilio Handlers
    const handleTwilioChange = (field, value) => {
        setTwilioConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleVerifyTwilio = async () => {
        setTwilioVerifying(true);
        setTwilioStatus(null);
        try {
            await adminApi.verifyTwilioConnection({
                accountSid: twilioConfig.accountSid,
                authToken: twilioConfig.authToken
            });
            setTwilioStatus('success');
            setSnackbar({ open: true, message: 'Twilio connection verified successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setTwilioStatus('error');
            setSnackbar({ open: true, message: `Twilio verification failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setTwilioVerifying(false);
        }
    };

    const handleSaveTwilio = async () => {
        setTwilioSaving(true);
        try {
            await adminApi.updateTwilioConfig(twilioConfig);
            setSnackbar({ open: true, message: 'Twilio configuration updated successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Twilio update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setTwilioSaving(false);
        }
    };

    // Slack Handlers
    const handleSlackChange = (field, value) => {
        setSlackConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleVerifySlack = async () => {
        setSlackVerifying(true);
        setSlackStatus(null);
        try {
            await adminApi.verifySlackConnection({
                botToken: slackConfig.botToken,
                channel: slackConfig.defaultChannel,
            });
            setSlackStatus('success');
            setSnackbar({ open: true, message: 'Slack connection verified — test message sent!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSlackStatus('error');
            setSnackbar({ open: true, message: `Slack verification failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSlackVerifying(false);
        }
    };

    const handleSaveSlack = async () => {
        setSlackSaving(true);
        try {
            await adminApi.updateSlackConfig(slackConfig);
            setSnackbar({ open: true, message: 'Slack configuration updated successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Slack update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSlackSaving(false);
        }
    };

    const handleLoadSlackHistory = async () => {
        setSlackHistoryLoading(true);
        try {
            const data = await adminApi.getSlackNotificationHistory(50);
            setSlackHistory(data.notifications || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to load history: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSlackHistoryLoading(false);
        }
    };

    // --- Ad Platform Handlers ---
    const handleAdPlatformFormChange = (platformId, field, value) => {
        setAdPlatformFormData(prev => ({
            ...prev,
            [platformId]: {
                ...prev[platformId],
                [field]: value
            }
        }));
    };

    const handleSaveAdPlatform = async (platformId) => {
        setAdPlatformSaving(prev => ({ ...prev, [platformId]: true }));
        try {
            await adminApi.updateAdPlatformConfig(platformId, adPlatformFormData[platformId]);
            const platformName = PLATFORM_NAMES[platformId] || platformId;
            setSnackbar({ open: true, message: `${platformName} configuration saved!`, severity: 'success' });
            loadConfigs();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Save failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setAdPlatformSaving(prev => ({ ...prev, [platformId]: false }));
        }
    };

    const handleVerifyAdPlatform = async (platformId) => {
        setAdPlatformVerifying(prev => ({ ...prev, [platformId]: true }));
        setAdPlatformStatus(prev => ({ ...prev, [platformId]: null }));
        try {
            const result = await adminApi.verifyAdPlatformConfig(platformId);
            setAdPlatformStatus(prev => ({ ...prev, [platformId]: result.success ? 'success' : 'error' }));
            setSnackbar({
                open: true,
                message: result.success ? result.message : `Verification failed: ${result.error}`,
                severity: result.success ? 'success' : 'error'
            });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setAdPlatformStatus(prev => ({ ...prev, [platformId]: 'error' }));
            setSnackbar({ open: true, message: `Verification failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setAdPlatformVerifying(prev => ({ ...prev, [platformId]: false }));
        }
    };

    const handleDeleteAdPlatform = async (platformId) => {
        const platformName = PLATFORM_NAMES[platformId] || platformId;
        if (!window.confirm(`Are you sure you want to delete ${platformName} credentials?`)) {
            return;
        }
        try {
            await adminApi.deleteAdPlatformConfig(platformId);
            setSnackbar({ open: true, message: `${platformName} credentials deleted.`, severity: 'success' });
            loadConfigs();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Delete failed: ${errorInfo.message}`, severity: 'error' });
        }
    };

    const handleToggleAdPlatformExpanded = (platformId) => {
        setAdPlatformExpanded(prev => ({ ...prev, [platformId]: !prev[platformId] }));
    };

    const handleToggleAdPlatformSecret = (platformId, field) => {
        setShowAdPlatformSecrets(prev => ({
            ...prev,
            [`${platformId}-${field}`]: !prev[`${platformId}-${field}`]
        }));
    };

    const handleExportAdPlatform = async (platformId) => {
        setAdPlatformExporting(prev => ({ ...prev, [platformId]: true }));
        try {
            const blob = await adminApi.exportAdPlatformConfig(platformId);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${platformId}-config-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setSnackbar({ open: true, message: `${platformId} configuration exported successfully!`, severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Export failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setAdPlatformExporting(prev => ({ ...prev, [platformId]: false }));
        }
    };

    // b: Platform status change handler (AC2)
    const handlePlatformStatusChange = async (platformId, newStatus) => {
        const platform = adPlatforms[platformId];
        const platformName = PLATFORM_NAMES[platformId] || platformId;

        // Show confirmation dialog for disabling
        if (newStatus === 'disabled' && (platform?.connectedUsers || 0) > 0) {
            setDisableConfirmDialog({ platformId, platformName, connectedUsers: platform.connectedUsers, newStatus });
            return;
        }

        await executePlatformStatusChange(platformId, newStatus);
    };

    const executePlatformStatusChange = async (platformId, newStatus) => {
        const platformName = PLATFORM_NAMES[platformId] || platformId;
        setStatusSaving(prev => ({ ...prev, [platformId]: true }));
        try {
            await adminApi.updateAdPlatformConfig(platformId, { status: newStatus });
            setSnackbar({ open: true, message: `${platformName} status updated to ${newStatus}.`, severity: 'success' });
            loadConfigs();
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Status update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setStatusSaving(prev => ({ ...prev, [platformId]: false }));
            setDisableConfirmDialog(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const tabs = TAB_CONFIG.map(t => ({ label: t.label, icon: t.icon }));

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure external service integrations.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error.message}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                    <StyledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        tabs={tabs}
                        ariaLabel="integrations tabs"
                    />
                </Box>

                <Box sx={{ p: 3 }}>
                    {/* Payments Tab (Stripe) */}
                    <TabPanel value={activeTab} index={0} sx={{ pt: 0 }}>
                        <StripeTab
                            config={stripeConfig}
                            onConfigChange={handleStripeChange}
                            onSave={handleSaveStripe}
                            onVerify={handleVerifyStripe}
                            saving={stripeSaving}
                            verifying={stripeVerifying}
                            status={stripeStatus}
                            showSecret={showStripeSecret}
                            onToggleSecret={() => setShowStripeSecret(!showStripeSecret)}
                        />
                    </TabPanel>

                    {/* Email Tab (SendGrid) */}
                    <TabPanel value={activeTab} index={1} sx={{ pt: 0 }}>
                        <SendGridTab
                            config={sendgridConfig}
                            onConfigChange={handleSendGridChange}
                            onSave={handleSaveSendGrid}
                            onVerify={handleVerifySendGrid}
                            saving={sendgridSaving}
                            verifying={sendgridVerifying}
                            status={sendgridStatus}
                            showSecret={showSendgridKey}
                            onToggleSecret={() => setShowSendgridKey(!showSendgridKey)}
                        />
                    </TabPanel>

                    { /* SMS Tab (Twilio) */}
                    <TabPanel value={activeTab} index={2} sx={{ pt: 0 }}>
                        <TwilioTab
                            config={twilioConfig}
                            onConfigChange={handleTwilioChange}
                            onSave={handleSaveTwilio}
                            onVerify={handleVerifyTwilio}
                            saving={twilioSaving}
                            verifying={twilioVerifying}
                            status={twilioStatus}
                            showSecret={showTwilioSecret}
                            onToggleSecret={() => setShowTwilioSecret(!showTwilioSecret)}
                        />
                    </TabPanel>

                    {/* Ad Platforms Tab */}
                    <TabPanel value={activeTab} index={3} sx={{ pt: 0 }}>
                        <AdPlatformsTab
                            platforms={adPlatforms}
                            formData={adPlatformFormData}
                            expanded={adPlatformExpanded}
                            saving={adPlatformSaving}
                            verifying={adPlatformVerifying}
                            exporting={adPlatformExporting}
                            status={adPlatformStatus}
                            showSecrets={showAdPlatformSecrets}
                            healthData={healthData}
                            analytics={analytics}
                            statusSaving={statusSaving}
                            onFormChange={handleAdPlatformFormChange}
                            onSave={handleSaveAdPlatform}
                            onVerify={handleVerifyAdPlatform}
                            onDelete={handleDeleteAdPlatform}
                            onExport={handleExportAdPlatform}
                            onToggleExpanded={handleToggleAdPlatformExpanded}
                            onToggleSecret={handleToggleAdPlatformSecret}
                            onStatusChange={handlePlatformStatusChange}
                        />
                    </TabPanel>

                    { /* Slack Tab */}
                    <TabPanel value={activeTab} index={4} sx={{ pt: 0 }}>
                        <SlackTab
                            config={slackConfig}
                            onConfigChange={handleSlackChange}
                            onSave={handleSaveSlack}
                            onVerify={handleVerifySlack}
                            saving={slackSaving}
                            verifying={slackVerifying}
                            status={slackStatus}
                            showSecret={showSlackSecret}
                            onToggleSecret={() => setShowSlackSecret(!showSlackSecret)}
                            notificationHistory={slackHistory}
                            onLoadHistory={handleLoadSlackHistory}
                            loadingHistory={slackHistoryLoading}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={5} sx={{ pt: 0 }}>
                        <McpCheckerTab />
                    </TabPanel>
                </Box>
            </Paper>

            {/* Disable Confirmation Dialog (AC2) */}
            <Dialog
                open={!!disableConfirmDialog}
                onClose={() => setDisableConfirmDialog(null)}
            >
                <DialogTitle>Disable {disableConfirmDialog?.platformName}?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Disabling {disableConfirmDialog?.platformName} will prevent {disableConfirmDialog?.connectedUsers} connected user{disableConfirmDialog?.connectedUsers !== 1 ? 's' : ''} from syncing.
                        Existing connections will be preserved.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisableConfirmDialog(null)}>Cancel</Button>
                    <Button
                        onClick={() => executePlatformStatusChange(disableConfirmDialog.platformId, disableConfirmDialog.newStatus)}
                        color="error"
                        variant="contained"
                    >
                        Disable
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default IntegrationsView;
